import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, ChevronDown, ChevronsUpDown, Languages, LogOut, Menu as IconeMenu, Moon, PanelLeftClose, PanelLeftOpen, Sun, UsersRound, X } from 'lucide-react';
import { MENUS } from '../nucleo/menus';
import { MarcaDoMenu } from '../componentes/marca';
import { destinoDoMenu, modulosDoMenu, type Acesso } from '../nucleo/modulos-web';
import { ICONES_SUBSISTEMAS, useSubsistema } from '../nucleo/subsistemas';
import { useSessao } from '../nucleo/sessao';
import { ProvedorAcaoPrimaria } from '../componentes/acao-primaria';
import { useEsc, useFocoInicial } from '../componentes/sobreposicao';
import { LimiteErro } from '../componentes/limite-erro';
import { Tour } from '../componentes/tour';
import { Menu, type ItemMenu } from '../componentes/menu';
import { BotaoDiscreto, ControlesDiscreto, PreferenciasDiscreto, VigiaDiscreto } from '../componentes/discreto';
import { anunciarOcultos } from '../componentes/sensivel';
import { email, type Idioma } from '../nucleo/formato';
import { BarraSuperior, EncaixesBarra } from './barra/BarraSuperior';
import { AtalhoRapido } from './barra/AtalhoRapido';
import { ItemMenuNotificacoes, SeloNotificacoes, VigiaNotificacoes } from '../nucleo/notificacoes/Sino';
import { FaixaAmbiente } from './FaixaAmbiente';
import { ModalSimularUsuario, podeSimular } from './simular-usuario';
import { useEspiar, useMenuRecolhido } from './menu-lateral';
import { PularParaConteudo } from '../componentes/pular-conteudo';
import { AreaDoConteudo, ProvedorTelaDividida, useEstadoTelaDividida, useMenuDaDivisao } from './tela-dividida';
import { CascaNova } from './CascaNova';
import { AvatarUsuario } from '../componentes/avatar-usuario';
import { mostrarWorkspaces } from './workspaces-perfil';

/**
 * Módulos web instalados e com ao menos uma aba permitida, por menu. Fase 2 (W6, L1): com as abas projetadas de outros
 * módulos (`AbaWeb.menu`), filtradas pela permissão da própria aba.
 */
export function useModulosDoMenu() {
  const acesso = useAcessoNavegacao();
  return (menu: string) => modulosDoMenu(menu, acesso);
}

/**
 * D1 (sub-sistemas): o acesso da sessão com o filtro do sub-sistema atual (só a navegação: menu lateral, abas e destino do
 * item). Sem a configuração ligada, é a sessão como sempre. U15 F5: leva o modo simples junto (o menu lateral esconde as abas avançadas também com sub-sistemas).
 */
export function useAcessoNavegacao(): Acesso {
  const s = useSessao();
  const { naNavegacao } = useSubsistema();
  return naNavegacao
    ? { instalado: s.instalado, pode: s.pode, paginaLiberada: s.paginaLiberada, modo: s.modo, plataforma: s.plataforma, todasAsEmpresas: s.todasAsEmpresas, modoSimples: s.modoSimples, naNavegacao }
    : s;
}

/** D1: caminhos que abrem sem a escolha do sub-sistema (a própria tela de escolha, Minha conta e o link externo embutido). */
export const livreDaEscolha = (pathname: string) => pathname === '/subsistemas' || pathname === '/conta' || pathname.startsWith('/conta/') || pathname.startsWith('/externo/');

/**
 * D1 (sub-sistemas): item do menu lateral com o sub-sistema atual, que leva à tela inicial de escolha ("Trocar empresa ou
 * sub-sistema"). Só com a configuração ligada e mais de uma escolha possível (sub-sistemas ou links externos).
 */
function ItemSubsistema({ compacto, aoNavegar }: { compacto?: boolean; aoNavegar?: () => void }) {
  const { t } = useTranslation();
  const sub = useSubsistema();
  const local = useLocation();
  if (!sub.ativo || sub.opcoes.length + sub.links.length < 2) return null;
  const nome = sub.atual ? t(`subsistemas.nomes.${sub.atual}`) : t('subsistemas.escolher');
  const Icone = (sub.atual && ICONES_SUBSISTEMAS[sub.atual]) || ArrowLeftRight;
  const ativo = local.pathname === '/subsistemas';
  const rotulo = sub.atual ? t('subsistemas.itemMenu', { nome }) : t('subsistemas.trocar');
  return (
    <Link
      to="/subsistemas"
      onClick={aoNavegar}
      aria-current={ativo ? 'page' : undefined}
      aria-label={rotulo}
      data-item-menu="subsistemas"
      title={rotulo}
      className={`relative flex min-h-11 items-center gap-3 rounded-lg border-l-3 py-2 lg:min-h-0 text-sm transition-colors ${compacto ? 'justify-center px-0' : 'px-3'} ${ativo ? 'border-acento-forte bg-sup-3 font-semibold text-tinta-1' : 'border-transparent font-medium text-tinta-2 hover:bg-sup-2 hover:text-tinta-1'}`}
    >
      <Icone className="size-4 shrink-0" aria-hidden />
      <span className={compacto ? 'sr-only' : 'min-w-0 flex-1 truncate'} title={rotulo}>{nome}</span>
      {sub.atual && <ChevronsUpDown className={`size-4 shrink-0 text-tinta-3 ${compacto ? 'hidden' : ''}`} aria-hidden />}
    </Link>
  );
}

/** Seletor de workspace (r7 visual B1): o `Menu` do núcleo, com setas, Esc e clique fora fechando e o foco de volta ao botão. */
function SeletorWorkspace({ compacto }: { compacto?: boolean }) {
  const s = useSessao();
  const { t } = useTranslation();
  // Val1 (28/10): o Sandbox sai com o selo "Teste" (no lugar do sufixo "sandbox" solto).
  const sufixo = (sandbox: boolean) => (sandbox ? ` (${t('casca.teste')})` : '');
  const itens: ItemMenu[] = s.workspaces.map((w) => ({
    chave: w.workspace_id,
    rotulo: `${w.tenant_nome} / ${w.workspace_nome}${sufixo(w.sandbox)}`,
    atual: w.workspace_id === s.workspace.id,
    aoClicar: () => {
      if (w.workspace_id !== s.workspace.id) void s.trocarWorkspace(w.workspace_id);
    },
  }));
  const nomeWorkspace = `${s.workspace.nome}${sufixo(s.workspace.sandbox)}`;
  // Val1 (28/10): com um workspace só, nada de seletor nem de nome de workspace: fica só a identificação da empresa.
  if (!mostrarWorkspaces(s.workspaces)) {
    return (
      <div data-tour="workspace" data-sem-workspaces="" className={`flex w-full min-w-0 items-center gap-3 rounded-lg border border-borda bg-sup-2 text-left ${compacto ? 'justify-center p-1.5' : 'px-3 py-2'}`}>
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-acento text-sm font-bold text-acento-tinta">{s.workspace.tenant_nome.slice(0, 1).toUpperCase()}</span>
        <span className={compacto ? 'sr-only' : 'min-w-0 flex-1'}>
          <span className="block truncate text-sm font-semibold" title={s.workspace.tenant_nome}>
            {s.workspace.tenant_nome}
          </span>
        </span>
      </div>
    );
  }
  return (
    <div data-tour="workspace">
      <Menu
        rotulo={t('shell.trocarWorkspace')}
        itens={itens}
        classePainel="left-0 w-60"
        classeGatilho={`flex w-full min-w-0 items-center gap-3 rounded-lg border border-borda bg-sup-2 text-left hover:bg-sup-3 ${compacto ? 'justify-center p-1.5' : 'px-3 py-2'}`}
        gatilho={
          <>
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-acento text-sm font-bold text-acento-tinta">{s.workspace.tenant_nome.slice(0, 1).toUpperCase()}</span>
            <span className={compacto ? 'sr-only' : 'min-w-0 flex-1'}>
              <span className="block truncate text-sm font-semibold" title={s.workspace.tenant_nome}>{s.workspace.tenant_nome}</span>
              <span className="block truncate text-xs text-tinta-3" title={nomeWorkspace}>
                {nomeWorkspace}
              </span>
            </span>
            <ChevronsUpDown className={`size-4 shrink-0 text-tinta-3 ${compacto ? 'hidden' : ''}`} aria-hidden />
          </>
        }
      />
    </div>
  );
}

/** Botão de recolher e expandir o menu lateral (desktop): `aria-expanded` diz se o menu está expandido. */
function BotaoRecolher({ recolhido, aoAlternar, controla }: { recolhido: boolean; aoAlternar: () => void; controla: string }) {
  const { t } = useTranslation();
  const rotulo = recolhido ? t('barra.expandirMenu') : t('barra.recolherMenu');
  const Icone = recolhido ? PanelLeftOpen : PanelLeftClose;
  return (
    <button type="button" data-recolher-menu aria-expanded={!recolhido} aria-controls={controla} aria-label={rotulo} title={rotulo} onClick={aoAlternar} className="rounded-md border-2 border-transparent p-1.5 text-tinta-3 hover:bg-sup-2 hover:text-tinta-1">
      <Icone className="size-4" aria-hidden />
    </button>
  );
}

const IDIOMAS: Idioma[] = ['pt', 'en', 'es'];

/**
 * Fase 2 (W31, item 3): seletor de idioma do rodapé, o `Menu` do núcleo (setas, Esc, clique fora, foco de volta). Aberto, o
 * gatilho mostra a sigla (PT, EN, ES) como antes; recolhido, o ícone de idioma. É o MESMO botão nos dois estados (só as
 * classes e o conteúdo mudam), para o foco não cair no body quando o trilho espia.
 */
function SeletorIdioma({ compacto }: { compacto?: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  const atual = s.usuario.idioma;
  const itens: ItemMenu[] = IDIOMAS.map((i) => ({ chave: i, rotulo: t(`shell.idiomas.${i}`), atual: i === atual, aoClicar: () => void s.preferencias({ idioma: i }) }));
  const rotulo = t('shell.idiomaAtual', { idioma: t(`shell.idiomas.${atual}`) });
  return (
    <div data-seletor-idioma title={rotulo}>
      <Menu
        rotulo={rotulo}
        itens={itens}
        classePainel="left-0 w-36"
        classeGatilho={
          compacto
            ? 'rounded-md border-2 border-transparent p-1.5 text-tinta-3 hover:bg-sup-2 hover:text-tinta-1'
            : 'flex h-11 items-center gap-1 rounded-md border border-borda-campo bg-campo px-2 text-xs text-tinta-2 hover:bg-sup-3 lg:h-8 lg:px-1.5'
        }
        gatilho={
          <>
            <Languages className={compacto ? 'size-4' : 'hidden'} aria-hidden />
            <span className={compacto ? 'sr-only' : 'font-medium uppercase'} aria-hidden>
              {atual}
            </span>
            <ChevronDown className={compacto ? 'hidden' : 'size-3'} aria-hidden />
          </>
        }
      />
    </div>
  );
}

/**
 * Menu lateral. `compacto`: trilho de ícones do desktop recolhido (rótulos só para leitor de tela e no title). Os
 * mesmos elementos existem nos dois estados (só as classes mudam), para o foco nunca cair no body ao espiar.
 * Fase 2 (W31): o botão de recolher fica no topo (onde estavam as preferências do modo discreto), o olho e as preferências
 * descem para os controles do rodapé, e "Notificações" é item do menu (o sino saiu da barra superior).
 */
function Sidebar({ aoNavegar, discreto, compacto, recolher }: { aoNavegar?: () => void; discreto?: boolean; compacto?: boolean; recolher?: { recolhido: boolean; aoAlternar: () => void } }) {
  const { t } = useTranslation();
  const s = useSessao();
  const acesso = useAcessoNavegacao();
  const doMenu = useModulosDoMenu();
  const local = useLocation();
  // Lote 19 (item 13): com a tela dividida, o item ativo e o destino do clique são os do painel que tem o foco.
  const divisao = useMenuDaDivisao(local.pathname, aoNavegar);
  const itens = MENUS.filter((m) => m.chave === 'inicio' || doMenu(m.chave).length > 0);
  return (
    <nav id={recolher ? 'menu-lateral' : undefined} aria-label={t('comum.menu')} className={`flex h-full flex-col gap-4 ${compacto ? 'items-stretch p-2' : 'p-4'}`}>
      <div className={`flex gap-2 px-1 ${compacto ? 'flex-col items-center' : 'items-center'}`}>
        {/* Fase 2 (W4): marca do cliente (nome e logo de Configurações > Aparência), ou a do produto. */}
        <MarcaDoMenu compacto={compacto} />
        {/* Fase 2 (W31): o botão de recolher no lugar das preferências do modo discreto (desktop). */}
        {recolher && (
          <div className={compacto ? '' : 'ml-auto'}>
            <BotaoRecolher recolhido={recolher.recolhido} aoAlternar={recolher.aoAlternar} controla="menu-lateral" />
          </div>
        )}
      </div>
      <SeletorWorkspace compacto={compacto} />
      <ul className="flex flex-1 flex-col gap-0.5 overflow-y-auto" data-tour="sidebar" onClickCapture={divisao.aoClicar} onContextMenu={divisao.aoContexto}>
        {itens.map((m, idx) => {
          const Icone = m.icone;
          // Primeiro módulo útil (depois do Início): fallback do passo "primeira ação" do tour.
          const marca = m.tour ?? (idx === 1 ? 'primeiro-modulo' : undefined);
          const destino = m.chave === 'inicio' ? '/' : (destinoDoMenu(m.chave, acesso) ?? `/${m.chave}`);
          const ativo = m.chave === 'inicio' ? divisao.pathname === '/' : divisao.pathname === destino || divisao.pathname.startsWith(`/${m.chave}/`);
          const rotulo = t(`menus.${m.chave}`);
          return (
            <Fragment key={m.chave}>
              <li data-tour={marca}>
                {/* r7 visual M4: o item ativo não depende só da cor do fundo (1,07 a 1,20:1): barra de 3px em acento-forte (3:1 ou mais,
                    tests/contraste.test.ts) e aria-current="page" também quando a rota é de outro módulo do mesmo menu. */}
                <Link
                  to={destino}
                  onClick={aoNavegar}
                  aria-current={ativo ? 'page' : undefined}
                  data-item-menu={m.chave}
                  title={compacto ? rotulo : undefined}
                  className={`relative flex min-h-11 items-center gap-3 rounded-lg border-l-3 py-2 lg:min-h-0 text-sm transition-colors ${compacto ? 'justify-center px-0' : 'px-3'} ${ativo ? 'border-acento-forte bg-sup-3 font-semibold text-tinta-1' : 'border-transparent font-medium text-tinta-2 hover:bg-sup-2 hover:text-tinta-1'}`}
                >
                  <Icone className="size-4 shrink-0" aria-hidden />
                  <span className={compacto ? 'sr-only' : 'min-w-0 flex-1'}>{rotulo}</span>
                  {/* Fase 2 (W15): selo do módulo no item (não lidas do chat da equipe no Comunicação). */}
                  {doMenu(m.chave).map((mod) => mod.seloMenu && <mod.seloMenu key={mod.slug} compacto={compacto} />)}
                </Link>
              </li>
              {/* Fase 2 (W31, item 2): Notificações logo depois do Início, com o selo de não lidas. */}
              {m.chave === 'inicio' && (
                <li>
                  <ItemMenuNotificacoes compacto={compacto} aoNavegar={aoNavegar} />
                </li>
              )}
              {/* D1 (sub-sistemas): "Trocar empresa ou sub-sistema", comum a todos, logo depois das Notificações. */}
              {m.chave === 'inicio' && (
                <li>
                  <ItemSubsistema compacto={compacto} aoNavegar={aoNavegar} />
                </li>
              )}
            </Fragment>
          );
        })}
      </ul>
      {divisao.menu}
      <Rodape compacto={compacto} discreto={discreto} />
      <p className={`numero px-1 text-xs text-tinta-3 ${compacto ? 'hidden' : ''}`}>{s.versao.slice(0, 7)}</p>
    </nav>
  );
}

/**
 * Rodapé do menu lateral: a pessoa (Minha conta) e, depois de uma divisória (W31, token `borda`), a linha dos controles:
 * tema, idioma, modo discreto (o olho e as preferências, só no desktop: no celular o olho fica na barra do topo) e Sair.
 * Recolhido, os controles empilham; o idioma vira ícone que abre as opções (W31).
 */
function Rodape({ compacto, discreto }: { compacto?: boolean; discreto?: boolean }) {
  const { t } = useTranslation();
  const s = useSessao();
  return (
    <div className="flex flex-col gap-2 border-t border-borda pt-3" data-rodape-menu>
      <div className={`flex items-center gap-2 px-1 ${compacto ? 'justify-center' : ''}`}>
        {/* Minha conta (r9 spec B5): perfil, tema, idioma e as preferências do modo discreto. */}
        <Link to="/conta" data-minha-conta title={compacto ? s.usuario.nome : undefined} className={`flex min-w-0 items-center gap-2 rounded-md hover:text-acento-forte ${compacto ? '' : 'flex-1'}`}>
          <AvatarUsuario usuarioId={s.usuario.id} versao={s.usuario.avatar_versao} className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sup-3 text-xs font-semibold" aria-hidden>
            {s.usuario.nome
              .split(' ')
              .map((p) => p[0])
              .slice(0, 2)
              .join('')
              .toUpperCase()}
          </AvatarUsuario>
          <span className={compacto ? 'sr-only' : 'min-w-0 flex-1'}>
            <span className="block truncate text-sm font-medium" title={s.usuario.nome}>{s.usuario.nome}</span>
            <span className="block truncate text-xs text-tinta-3" title={email(s.usuario.email)}>{anunciarOcultos(email(s.usuario.email))}</span>
            <span className="sr-only">{t('conta.titulo')}</span>
          </span>
        </Link>
      </div>
      {/* Fase 2 (W31): divisória entre a pessoa e a linha dos controles. */}
      <div data-divisoria-rodape className="border-t border-borda" aria-hidden />
      <div data-controles-rodape className={`flex items-center gap-1 ${compacto ? 'flex-col' : ''}`}>
        <button
          type="button"
          // Alternância (r6 visual B9): "Tema escuro" ligado ou desligado em aria-pressed; o title diz o tema atual.
          aria-label={t('tema.escuro')}
          aria-pressed={s.usuario.tema === 'escuro'}
          title={t('tema.atual', { tema: s.usuario.tema === 'escuro' ? t('comum.temaEscuro') : t('comum.temaClaro') })}
          onClick={() => s.preferencias({ tema: s.usuario.tema === 'escuro' ? 'claro' : 'escuro' })}
          // Ligado (tema escuro) com borda de 3:1, além do ícone: o estado não depende só da forma (r6 visual B1/B9).
          className={`rounded-md border-2 p-3 text-tinta-3 hover:bg-sup-2 hover:text-tinta-1 lg:p-1.5 ${s.usuario.tema === 'escuro' ? 'border-tinta-3' : 'border-transparent'}`}
        >
          {s.usuario.tema === 'escuro' ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>
        <SeletorIdioma compacto={compacto} />
        {/* val4 (29/10): "Simular usuário de teste" (só com `admin.simular_usuario` e fora de uma simulação). */}
        {podeSimular(s) && <BotaoSimular />}
        {/* Modo discreto (FUN 2026-10-02; W31: desceu do topo para o rodapé): o olho e as preferências, no desktop. */}
        {discreto && (
          <>
            <BotaoDiscreto />
            <PreferenciasDiscreto />
          </>
        )}
        {/* Sair: o mesmo comportamento; W31 só muda o layout (ícone com nome e title, para caber com o discreto na linha). */}
        <button
          type="button"
          onClick={() => s.sair()}
          disabled={s.saindo}
          aria-busy={s.saindo}
          aria-label={t('comum.sair')}
          title={t('comum.sair')}
          data-sair
          className={`rounded-md border-2 border-transparent p-3 text-tinta-3 hover:bg-sup-2 hover:text-tinta-1 disabled:cursor-not-allowed lg:p-1.5 disabled:opacity-50 ${compacto ? '' : 'ml-auto'}`}
        >
          <LogOut className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** val4 (29/10): o item do perfil na casca antiga (ícone na linha dos controles, com nome no title e no aria-label). */
function BotaoSimular() {
  const { t } = useTranslation();
  const [aberto, setAberto] = useState(false);
  const botao = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={botao}
        type="button"
        data-simular-item=""
        aria-haspopup="dialog"
        aria-label={t('simulacao.item')}
        title={t('simulacao.item')}
        onClick={() => setAberto(true)}
        className="rounded-md border-2 border-transparent p-3 text-tinta-3 hover:bg-sup-2 hover:text-tinta-1 lg:p-1.5"
      >
        <UsersRound className="size-4" aria-hidden />
      </button>
      <ModalSimularUsuario
        aberto={aberto}
        aoFechar={() => {
          setAberto(false);
          botao.current?.focus();
        }}
      />
    </>
  );
}

/**
 * Fase 2 (W17, K11): selos dos menus visíveis (não lidas do chat da equipe) no canto do botão do menu do celular, da
 * mesma fonte do selo do item. O texto do selo descreve o botão (`aria-describedby`), já que o rótulo é "Abrir menu".
 */
function SelosDoBotaoMenu({ id }: { id: string }) {
  const doMenu = useModulosDoMenu();
  const selos = MENUS.flatMap((m) => doMenu(m.chave)).filter((mod, i, l) => mod.seloMenu && l.findIndex((x) => x.slug === mod.slug) === i);
  // Fase 2 (W31): as não lidas das notificações também (o sino saiu do cabeçalho do celular e virou item do menu).
  return (
    <span id={id} data-selos-botao-menu>
      <SeloNotificacoes noBotao />
      {selos.map((mod) => mod.seloMenu && <mod.seloMenu key={mod.slug} noBotao />)}
    </span>
  );
}

/**
 * Casca autenticada: sidebar de 256px (F5) recolhível no desktop (trilho de 64px que espia por cima com o mouse parado ou
 * com o foco do teclado; fase 2, W1 e W31), gaveta no celular, barra superior (só com o seletor de empresa desde a W31, e
 * só quando ele aparece) e a faixa de ambiente.
 */
export function Shell({ rotas }: { rotas?: () => ReactNode }) {
  // CRM no novo desenho (N7): com a chave do workspace ligada, a casca do clone do RD; senão, a de sempre.
  return useSubsistema().novoDesenho ? <CascaNova rotas={rotas} /> : <CascaAtual rotas={rotas} />;
}

function CascaAtual({ rotas }: { rotas?: () => ReactNode }) {
  const { t } = useTranslation();
  const s = useSessao();
  // Lote 19 (item 13): tela dividida (o painel ao lado usa as mesmas rotas do conteúdo, que o App entrega).
  const divisao = useEstadoTelaDividida(s.usuario.id, !!rotas);
  const [gaveta, setGaveta] = useState(false);
  const local = useLocation();
  const sub = useSubsistema();
  // D1: com sub-sistemas ligados e mais de um à escolha, a tela inicial pede a escolha antes (depois do login e ao trocar
  // de empresa); o destino pedido volta depois da escolha.
  const escolherAntes = sub.precisaEscolher && !livreDaEscolha(local.pathname);
  const [tour, setTour] = useState(false);
  const [recolhido, alternarMenu] = useMenuRecolhido(s.usuario.id);
  // No tour o menu abre à força (o holofote aponta para os itens com rótulo), como já faz com a gaveta.
  const aberto = !recolhido || tour;
  const { espiando, props: espiar } = useEspiar(!aberto);
  useEffect(() => setGaveta(false), [local.pathname]);
  // Gaveta (< 1024px) como diálogo: Esc fecha, foco entra e fica preso, o fundo não rola e o foco volta ao botão do menu.
  // Com o tour aberto (r7 visual ALTO), a gaveta só serve de vitrine para o holofote: fica inerte, sem trap e sem Esc próprios,
  // e o tour continua sendo a camada do topo (antes a gaveta reaberta no passo Admin empilhava o trap por cima do tour e o
  // Tab circulava nela, sem alcançar Pular e Concluir).
  const fecharGaveta = useCallback(() => setGaveta(false), []);
  const gavetaInterativa = gaveta && !tour;
  useEsc(gavetaInterativa, fecharGaveta);
  const refGaveta = useFocoInicial(gavetaInterativa);
  const botaoMenu = useRef<HTMLButtonElement>(null);
  const abriuGaveta = useRef(false);
  useEffect(() => {
    if (gavetaInterativa) abriuGaveta.current = true;
    else if (!gaveta && abriuGaveta.current) {
      abriuGaveta.current = false;
      botaoMenu.current?.focus();
    }
  }, [gaveta, gavetaInterativa]);
  useEffect(() => {
    if (!s.usuario.onboarding_concluido) {
      const id = setTimeout(() => setTour(true), 600);
      return () => clearTimeout(id);
    }
  }, [s.usuario.onboarding_concluido]);

  return (
    <ProvedorTelaDividida value={divisao}>
    <ProvedorAcaoPrimaria>
      <PularParaConteudo />
      <FaixaAmbiente />
      <div className="flex min-h-full">
        <aside data-menu-lateral={aberto ? 'aberto' : espiando ? 'espiando' : 'recolhido'} className={`topo-faixa altura-sob-faixa sticky z-30 hidden shrink-0 lg:block ${aberto ? 'w-64' : 'w-16'}`}>
          <div {...espiar} className={`h-full border-r border-borda bg-sup-1 ${!aberto && espiando ? 'absolute inset-y-0 left-0 z-30 w-64 shadow-xl' : 'w-full'}`}>
            <Sidebar discreto compacto={!aberto && !espiando} recolher={{ recolhido, aoAlternar: alternarMenu }} />
          </div>
        </aside>
        {gaveta && (
          <div className="topo-faixa fixed inset-x-0 bottom-0 z-40 bg-veu lg:hidden" inert={tour} onClick={() => setGaveta(false)}>
            <div ref={refGaveta} data-gaveta data-sem-impressao role="dialog" aria-modal={!tour} aria-label={t('comum.menu')} tabIndex={-1} className="relative h-full w-64 max-w-full border-r border-borda bg-sup-1 focus:outline-none" onClick={(e) => e.stopPropagation()}>
              <button type="button" aria-label={t('comum.recolherMenu')} onClick={() => setGaveta(false)} className="absolute top-2 right-2 flex size-11 items-center justify-center rounded-lg text-tinta-3 hover:bg-sup-2 hover:text-tinta-1">
                <X className="size-5" />
              </button>
              <Sidebar aoNavegar={() => setGaveta(false)} />
            </div>
          </div>
        )}
        {/* Lote 19 (item 13): lado a lado, cada painel rola sozinho (a coluna fica na altura da janela). */}
        <div className={`flex min-w-0 flex-1 flex-col ${divisao.ativo && !divisao.emAbas ? 'topo-faixa altura-sob-faixa sticky' : ''}`}>
          <BarraSuperior />
          <header data-sem-impressao className="topo-faixa sticky z-20 border-b border-borda bg-fundo lg:hidden">
            <div className="flex items-center gap-3 px-4 py-2">
              <button
                ref={botaoMenu}
                type="button"
                aria-expanded={gaveta}
                aria-label={gaveta ? t('comum.recolherMenu') : t('comum.abrirMenu')}
                aria-describedby={gaveta ? undefined : 'selos-botao-menu'}
                onClick={() => setGaveta((x) => !x)}
                className="relative flex size-11 shrink-0 items-center justify-center rounded-lg text-tinta-2 hover:bg-sup-2"
              >
                {gaveta ? <X className="size-5" /> : <IconeMenu className="size-5" />}
                {!gaveta && <SelosDoBotaoMenu id="selos-botao-menu" />}
              </button>
              {/* Fase 2 (W14): a marca do cliente (nome e logo de Configurações > Aparência) também no cabeçalho do celular. */}
              {/* A marca parte de 6rem (o símbolo e o começo do nome sempre à vista), cresce no que sobra e corta antes dos ícones. */}
              <div className="flex min-w-0 flex-1 basis-24 items-center gap-3">
                <MarcaDoMenu cabecalho pequena />
              </div>
              {/* Review r16 visual M1: o seletor de empresa desceu para a segunda linha (a 390 e 360 px o nome sumia aqui). W31: o
                  sino saiu (Notificações é item do menu, com o selo no botão do menu). */}
              <div className="ml-auto flex min-w-0 items-center gap-2">
                {/* Modo discreto (FUN 2026-10-02): olho na barra superior do celular. */}
                <ControlesDiscreto tamanho="size-5" />
                {/* U15 F8: atalho rápido (tarefa e mensagem) em qualquer tela, também no celular. */}
                <AtalhoRapido celular />
              </div>
            </div>
            {/* Fase 2 (W31): a faixa de índices saiu (virou Financeiro > Índices). Com mais de uma empresa, o seletor ganha a linha
                de baixo, que some (`empty:hidden`) quando o workspace tem uma empresa só. */}
            <div data-linha-empresa="" className="flex min-w-0 items-center border-t border-borda px-4 py-1 empty:hidden">
              <EncaixesBarra celular="faixa" />
            </div>
          </header>
          <AreaDoConteudo d={divisao} rotas={rotas}>
          <main id="conteudo" tabIndex={-1} data-painel-conteudo="principal" data-painel-ativo={divisao.foco === 'principal' ? '' : undefined} aria-label={divisao.ativo ? t('divisao.painel', { n: 1 }) : undefined} className="mx-auto w-full max-w-7xl min-w-0 flex-1 px-4 py-6 focus:outline-none sm:px-6 lg:px-8">
            {/* Chave só de menu e módulo (r6 visual M1): trocar de aba não remonta a seção (o foco ficava no body); o erro de cada
                aba é contido pelo LimiteErro da própria Secao. */}
            <LimiteErro key={local.pathname.split('/').slice(0, 3).join('/')}>
              {escolherAntes ? <Navigate to={`/subsistemas?voltar=${encodeURIComponent(local.pathname + local.search)}`} replace /> : <Outlet />}
            </LimiteErro>
          </main>
          </AreaDoConteudo>
        </div>
      </div>
      <VigiaDiscreto />
      <VigiaNotificacoes />
      {tour && (
        <Tour
          menuAberto={gaveta}
          definirMenu={setGaveta}
          aoConcluir={() => {
            setTour(false);
            void s.preferencias({ onboarding_concluido: true });
          }}
        />
      )}
    </ProvedorAcaoPrimaria>
    </ProvedorTelaDividida>
  );
}
